import { isDefined, isNotDefined } from '@sgme/fp';

import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { espApi } from '@/store/esp/api';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { getStreamingConnectionStatus, signalRStatusUpdated } from '@/store/streaming/slice.ts';
import type { DurationInMilliseconds } from '@/types/date.ts';
import { SECOND_IN_MILLIS } from '@/utils/time.ts';

import { espStreamCancel, espStreamSubscribed, espStreamUnsubscribed } from './slice.ts';
import { getRequestMutationCacheKey } from './utils.ts';

export const registerEspListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    // unsubscribe tile from esp -> decrease refCount
    matcher: espStreamSubscribed.match,
    effect: async (action, api) => {
      const actualState = api.getState();
      const appDispatch = api.dispatch;

      const espId = `${action.payload.currencyPair}&&${action.payload.tiering}`;
      const espPrice = actualState.esp.streams[espId];

      if (isDefined(espPrice) && espPrice.status === 'PRICED') {
        // Maybe need to check refcount > 0
        return;
      }

      const signalRConnectionId = await getSignalRConnectionId(api);

      const currencyPair = action.payload.currencyPair;
      const tiering = action.payload.tiering;

      await waitSometingVeryImportant();

      await appDispatch(
        espApi.endpoints.requestEsp.initiate(
          {
            signalRConnectionId,
            currencyPair,
            tiering,
            tileId: action.payload.tileId,
            companyId: action.payload.bdrId,
          },
          { fixedCacheKey: getRequestMutationCacheKey(currencyPair, tiering) },
        ),
      );
    },
  });

  startAppListening({
    // unsubscribe tile from esp -> decrease refCount
    matcher: espStreamUnsubscribed.match,
    effect: async (action, api) => {
      const actualState = api.getState();
      const appDispatch = api.dispatch;

      const espId = `${action.payload.currencyPair}&&${action.payload.tiering}`;
      const esp = actualState.esp.streams[espId];

      const streamingConnectionStatus = getStreamingConnectionStatus(actualState);

      if (streamingConnectionStatus.status !== 'connected') {
        logger.logWarning('[registerEspListeners / espStreamUnsubscribed] SignalR not connected');
        return;
      }

      if (isNotDefined(esp)) {
        logger.logWarning(
          `[registerEspListeners / espStreamUnsubscribed] can't unsubscribe ESP stream because ESP not found ${espId} in redux store`,
        );
        return;
      }

      const { subscribedTileIds } = esp;

      if (subscribedTileIds.length === 0) {
        const currencyPair = action.payload.currencyPair;
        const tiering = action.payload.tiering;

        // Delete flux esp[espId]
        appDispatch(espStreamCancel({ currencyPair, tiering }));

        // Request /cancel
        await appDispatch(
          espApi.endpoints.cancelEsp.initiate({
            signalRConnectionId: streamingConnectionStatus.connectionId,
            currencyPair,
            tiering,
          }),
        );

        // Stop listening signalR for this esp
        await appDispatch(
          espApi.internalActions.removeMutationResult({
            fixedCacheKey: getRequestMutationCacheKey(currencyPair, tiering),
          }),
        );
      }
    },
  });

  startAppListening({
    matcher: signalRStatusUpdated.match,
    effect: async (action, api) => {
      if (action.payload.status !== 'connected') {
        return;
      }

      const actualState = api.getState();
      const appDispatch = api.dispatch;

      const streamKeys = Object.keys(actualState.esp.streams);
      const connectionId = action.payload.connectionId;

      if (streamKeys.length === 0) {
        return;
      }

      // mode réabonnement en mode batch
      await appDispatch(
        espApi.endpoints.refreshEsp.initiate(
          { dataStreamId: connectionId, streamKeys },
          { fixedCacheKey: 'refreshEsp' },
        ),
      )
        .unwrap()
        .catch((error) => {
          logger.logError('Streams refresh failed {message_s}', error);
          console.error('Streams refresh failed', error);
        });
    },
  });

  startAppListening({
    matcher: signalRStatusUpdated.match,
    effect: async (action, api) => {
      if (action.payload.status !== 'disconnected') {
        return;
      }

      const actualState = api.getOriginalState();
      const appDispatch = api.dispatch;

      const streamValues = Object.values(actualState.esp.streams);

      const streamingConnectionStatus = getStreamingConnectionStatus(actualState);

      if (
        streamValues.length === 0 ||
        !streamingConnectionStatus ||
        streamingConnectionStatus.status === 'disconnected'
      ) {
        return;
      }

      // only one of these remove mutations is enough but we cant which one to remove
      for (const esp of streamValues) {
        const currencyPair = esp.currencyPair;
        const tiering = esp.tiering;

        appDispatch(
          espApi.internalActions.removeMutationResult({
            fixedCacheKey: getRequestMutationCacheKey(currencyPair, tiering),
          }),
        );
      }

      appDispatch(espApi.internalActions.removeMutationResult({ fixedCacheKey: 'refreshEsp' }));
    },
  });
};

/**
 * HACK
 * Wait a tick to avoid the inversion between cancel ESP and request ESP when unsubscribing and subscribing quickly
 * It is an edge case but it happens when the user switch quickly between two ESP tierings on the same tile
 */
async function waitSometingVeryImportant() {
  await new Promise((resolve) => setTimeout(resolve, 30));
}

// this active wait covers the case of a tile subscribing before SignalR is connected: the "refresh" mutation
// requires an existing connection, so we wait for one instead of relying on it here.
async function getSignalRConnectionId(api: any): Promise<string> {
  let connectionId: string | null = null;

  while (isNotDefined(connectionId)) {
    const actualState = api.getState();
    const streamingConnectionStatus = getStreamingConnectionStatus(actualState);

    if (streamingConnectionStatus?.status !== 'connected') {
      await wait(2 * SECOND_IN_MILLIS);
      continue;
    }

    connectionId = streamingConnectionStatus.connectionId;
  }

  return connectionId;
}

async function wait(delay: DurationInMilliseconds) {
  await new Promise((resolve) => setTimeout(resolve, delay));
}
